import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Target, Utensils, Megaphone, FileCheck2, Paperclip,
  PhoneCall, Users2, CheckCircle2, CalendarDays,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const docUrl = (path) => {
  const t = localStorage.getItem("sc_token");
  return t && path ? `${BACKEND}/api/bd-board/doc/${path}?auth=${t}` : "";
};
const thisMonth = () => new Date().toISOString().slice(0, 7);
const today = () => new Date().toISOString().slice(0, 10);

function UserSelect({ rows, value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9" data-testid="bd-user-select"><SelectValue placeholder="BD member" /></SelectTrigger>
      <SelectContent>{rows.map((r) => <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function TargetsDialog({ open, onOpenChange, rows, onSaved }) {
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      for (const r of rows) {
        const v = vals[r.user_id];
        if (v !== undefined && Number(v) !== r.lnl_target) {
          await api.put("/bd-board/targets", { user_id: r.user_id, lnl_monthly_target: Number(v) });
        }
      }
      toast.success("L&L targets updated");
      onSaved(); onOpenChange(false); setVals({});
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="bd-targets-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Target className="h-4 w-4 text-brand" /> Lunch & Learn — monthly targets</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.user_id} className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-700">{r.name}</span>
              <Input type="number" min="0" className="h-8 w-20 text-center" value={vals[r.user_id] ?? r.lnl_target}
                onChange={(e) => setVals((v) => ({ ...v, [r.user_id]: e.target.value }))}
                data-testid={`bd-target-input-${r.user_id}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand/90 text-white gap-1.5" data-testid="bd-targets-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save targets
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDialog({ kind, open, onOpenChange, board, isMonitor, activityTypes, onSaved }) {
  const [f, setF] = useState({ date: today() });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const reset = () => { setF({ date: today() }); setFile(null); };
  const save = async () => {
    setSaving(true);
    try {
      if (kind === "lnl") {
        await api.post("/bd-board/lnl", f);
      } else if (kind === "activity") {
        await api.post("/bd-board/activities", f);
      } else {
        const fd = new FormData();
        fd.append("project", f.project || "");
        fd.append("consultant", f.consultant || "");
        fd.append("brand_product", f.brand_product || "");
        fd.append("spec_date", f.date || today());
        fd.append("status", f.status || "Submitted");
        fd.append("notes", f.notes || "");
        if (f.user_id) fd.append("user_id", f.user_id);
        if (file) fd.append("file", file);
        await api.post("/bd-board/specs", fd);
      }
      toast.success("Added");
      reset(); onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const titles = { lnl: "Log a Lunch & Learn session", activity: "Log a lead-gen activity", spec: "Log a specification / brand approval" };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid={`bd-add-${kind}-dialog`}>
        <DialogHeader><DialogTitle className="font-heading">{titles[kind]}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isMonitor && (
            <div className="space-y-1.5"><Label className="text-xs">BD member *</Label>
              <UserSelect rows={board?.rows || []} value={f.user_id || ""} onChange={(v) => setF({ ...f, user_id: v })} />
            </div>
          )}
          <div className="space-y-1.5"><Label className="text-xs">Date *</Label>
            <Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-9" data-testid={`bd-${kind}-date`} /></div>
          {kind === "lnl" && (
            <>
              <div className="space-y-1.5"><Label className="text-xs">Architect / consultant firm *</Label>
                <Input value={f.firm || ""} onChange={(e) => setF({ ...f, firm: e.target.value })} className="h-9" data-testid="bd-lnl-firm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Attendees</Label>
                <Input value={f.attendees || ""} onChange={(e) => setF({ ...f, attendees: e.target.value })} placeholder="e.g. 8 architects, principal designer" className="h-9" data-testid="bd-lnl-attendees" /></div>
            </>
          )}
          {kind === "activity" && (
            <div className="space-y-1.5"><Label className="text-xs">Activity type *</Label>
              <Select value={f.activity_type || ""} onValueChange={(v) => setF({ ...f, activity_type: v })}>
                <SelectTrigger className="h-9" data-testid="bd-activity-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(activityTypes || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {kind === "spec" && (
            <>
              <div className="space-y-1.5"><Label className="text-xs">Project *</Label>
                <Input value={f.project || ""} onChange={(e) => setF({ ...f, project: e.target.value })} className="h-9" data-testid="bd-spec-project" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Consultant / architect</Label>
                  <Input value={f.consultant || ""} onChange={(e) => setF({ ...f, consultant: e.target.value })} className="h-9" data-testid="bd-spec-consultant" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Brand / product specified</Label>
                  <Input value={f.brand_product || ""} onChange={(e) => setF({ ...f, brand_product: e.target.value })} className="h-9" data-testid="bd-spec-brand" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Tender / RFP document (proof)</Label>
                <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-9" data-testid="bd-spec-file" />
                <p className="text-[10px] text-slate-400">PDF, Word, Excel, image or ZIP · max 20 MB</p></div>
            </>
          )}
          <div className="space-y-1.5"><Label className="text-xs">Notes</Label>
            <Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} data-testid={`bd-${kind}-notes`} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid={`bd-${kind}-save`}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SECTIONS = [
  { key: "lnl", label: "Lunch & Learn", icon: Utensils, api: "/bd-board/lnl" },
  { key: "activity", label: "Lead-gen Activities", icon: Megaphone, api: "/bd-board/activities" },
  { key: "spec", label: "Spec & Brand Approvals", icon: FileCheck2, api: "/bd-board/specs" },
];

export default function BDBoard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isMonitor = ["sales_head", "super_admin"].includes(user?.role);
  const [month, setMonth] = useState(thisMonth());
  const [section, setSection] = useState("lnl");
  const [addOpen, setAddOpen] = useState(false);
  const [targetsOpen, setTargetsOpen] = useState(false);

  const { data: board, isLoading } = useQuery({
    queryKey: ["bd-board", month],
    queryFn: async () => (await api.get("/bd-board/board", { params: { month } })).data,
  });
  const sec = SECTIONS.find((s) => s.key === section);
  const { data: items = [] } = useQuery({
    queryKey: ["bd-items", section, month],
    queryFn: async () => (await api.get(sec.api, { params: { month } })).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["bd-board"] });
    qc.invalidateQueries({ queryKey: ["bd-items"] });
  };
  const del = async (id) => {
    try {
      await api.delete(`${sec.api}/${id}`);
      toast.success("Removed");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (id, status) => {
    try {
      await api.put(`/bd-board/specs/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading || !board) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div data-testid="bd-board-page">
      <PageHeader title="BD Board" subtitle="What Sales Head tracks for Business Development — calls, Lunch & Learn, lead-gen and specifications.">
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 w-[160px]" data-testid="bd-month-input" />
        {board.can_edit_targets && (
          <Button variant="outline" onClick={() => setTargetsOpen(true)} className="h-9 gap-1.5" data-testid="bd-set-targets-btn">
            <Target className="h-4 w-4" /> Set L&L targets
          </Button>
        )}
      </PageHeader>

      {/* Summary */}
      <Card className="p-0 overflow-hidden mb-5" data-testid="bd-summary-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left">
                {["BD Member", "Daily Calls", "Lunch & Learn", "Lead-gen Activities", "Leads Created", "Specs (Sub / Appr)"].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {board.rows.map((r, i) => (
                <tr key={r.user_id} data-testid={`bd-summary-row-${i}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-800">{r.name}</p>
                    <p className="text-[11px] text-slate-400">{r.region}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-sm font-mono font-semibold text-slate-800">
                      <PhoneCall className="h-3.5 w-3.5 text-brand" /> {r.calls}
                    </span>
                    <p className="text-[10px] text-slate-400">{r.calls_logged} logged · {r.calls_manual} manual</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-semibold text-slate-800">{r.lnl_count}<span className="text-slate-400 font-normal"> / {r.lnl_target || "—"}</span></span>
                      {r.lnl_target > 0 && (
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${r.lnl_count >= r.lnl_target ? "bg-emerald-500" : "bg-amber-400"}`}
                            style={{ width: `${Math.min(100, (r.lnl_count / r.lnl_target) * 100)}%` }} />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono font-semibold text-slate-800">{r.activities}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-sm font-mono font-semibold text-slate-800">
                      <Users2 className="h-3.5 w-3.5 text-brand" /> {r.leads_created}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono font-semibold text-slate-800">
                    {r.specs_submitted} / <span className="text-emerald-600">{r.specs_approved}</span>
                  </td>
                </tr>
              ))}
              {board.rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-400">No BD members found.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Section tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
          {SECTIONS.map((s) => (
            <button key={s.key} onClick={() => setSection(s.key)}
              className={`text-sm px-4 py-1.5 rounded-md font-medium flex items-center gap-1.5 transition-colors ${section === s.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid={`bd-section-${s.key}`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </div>
        <Button onClick={() => setAddOpen(true)} className="h-9 gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="bd-add-btn">
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>

      <Card className="p-0 overflow-hidden" data-testid={`bd-list-${section}`}>
        <div className="divide-y divide-slate-100">
          {items.map((it, i) => (
            <div key={it.id} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`bd-item-${i}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">
                  {section === "lnl" && it.firm}
                  {section === "activity" && it.activity_type}
                  {section === "spec" && it.project}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{it.date}</span>
                  <span>· {it.user_name}</span>
                  {section === "lnl" && it.attendees && <span>· {it.attendees}</span>}
                  {section === "spec" && it.consultant && <span>· {it.consultant}</span>}
                  {section === "spec" && it.brand_product && <span>· {it.brand_product}</span>}
                </p>
                {it.notes && <p className="text-xs text-slate-400 mt-1">{it.notes}</p>}
                {section === "spec" && it.document && (
                  <a href={docUrl(it.document.storage_path)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-brand hover:underline mt-1" data-testid={`bd-spec-doc-${i}`}>
                    <Paperclip className="h-3 w-3" /> {it.document.filename}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {section === "spec" && (
                  it.status === "Approved" ? (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3 w-3" /> Approved</Badge>
                  ) : (
                    <>
                      <Badge variant="outline" className="text-amber-600 border-amber-200">Submitted</Badge>
                      {(isMonitor || it.user_id === board.me) && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setStatus(it.id, "Approved")} data-testid={`bd-spec-approve-${i}`}>
                          Mark approved
                        </Button>
                      )}
                    </>
                  )
                )}
                {(isMonitor || it.user_id === board.me) && (
                  <button onClick={() => del(it.id)} className="h-7 w-7 inline-flex items-center justify-center rounded text-slate-300 hover:text-red-500 hover:bg-red-50" data-testid={`bd-del-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-center py-12 text-sm text-slate-400">Nothing logged for this month yet — tap "Add".</p>
          )}
        </div>
      </Card>

      <AddDialog kind={section} open={addOpen} onOpenChange={setAddOpen} board={board}
        isMonitor={isMonitor} activityTypes={board.activity_types} onSaved={refresh} />
      <TargetsDialog open={targetsOpen} onOpenChange={setTargetsOpen} rows={board.rows} onSaved={refresh} />
    </div>
  );
}
